import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FaX } from 'react-icons/fa6'
import Select from 'react-select'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { useDialogBox } from '../../../../hooks/useDialogBox'
import { useLocalStorage } from '../../../../hooks/useLocalStorage'
import { Role } from '../../../../interfaces/rolesEnum'
import { SelectOption } from '../../../../reducers/appReducer'
import { getAllPromotersCompanies } from '../../../../services/promotersCompaniesServices'
import { createUser, updateUser, User } from '../../../../services/userServices'
import { JWT_TOKEN_KEY_NAME } from '../../../../utils/constants'
import { getRolesSelectOptions } from '../../../../utils/getRolesSelectOptions'

interface ModalUpsertUserProps {
  onClose: () => void
  refetchList: () => void
  user?: User | null
}

const upsertUserFormSchema = z.object({
  email: z.string().email(),
  role: z.string(),
  subrole: z.string().optional().nullable(),
  manager_of_promoters_company_id: z.string().optional().nullable(),
  promoter_of_company_id: z.string().optional().nullable(),
})

type UpsertUserFormData = z.infer<typeof upsertUserFormSchema>

export function ModalUpsertUser({ onClose, refetchList, user }: ModalUpsertUserProps) {
  const [rolesSelect, setRolesSelect] = useState<SelectOption[]>([])
  const { profile } = useAppSelector(state => state.auth)

  const { showDialog } = useDialogBox()
  const [token] = useLocalStorage(JWT_TOKEN_KEY_NAME)

  const { register, control, handleSubmit, watch } = useForm<UpsertUserFormData>({
    resolver: zodResolver(upsertUserFormSchema),
    defaultValues: {
      email: user?.email,
      role: profile?.role === 'PROMOTER_MANAGER'
        ? 'PROMOTER'
        : user?.role,
      subrole: user?.subrole ?? 'NONE',
      manager_of_promoters_company_id: user?.manager_of_promoters_company_id,
      promoter_of_company_id: profile?.role === 'PROMOTER_MANAGER'
        ? profile.manager_of_promoters_company_id
        : user?.promoter_of_company_id,
    },
  })

  const selectedRole = watch('role') as keyof typeof Role

  async function handleSubmitUpsertUserForm(data: UpsertUserFormData) {
    let result
    if (user) {
      const manager_of_promoters_company_id = user.role === 'PROMOTER_MANAGER' && data.role !== 'PROMOTER_MANAGER'
        ? null
        : data.manager_of_promoters_company_id
      const promoter_of_company_id = user.role === 'PROMOTER' && data.role !== 'PROMOTER'
        ? null
        : data.promoter_of_company_id
      result = await updateUser({
        role: data.role as Role,
        subrole: data.subrole === 'NONE'
          ? null
          : data.subrole as Role,
        email: data.email,
        user_id: user.id,
        manager_of_promoters_company_id,
        promoter_of_company_id,
      }, token)
    } else {
      result = await createUser({
        email: data.email,
        role: data.role as Role,
        subrole: data.subrole === 'NONE'
          ? null
          : data.subrole as Role,
        manager_of_promoters_company_id: data.manager_of_promoters_company_id,
        promoter_of_company_id: data.promoter_of_company_id,
      }, token)
    }

    if (result) {
      if (result.success) {
        showDialog({
          title: 'Sucesso',
          type: 'success',
          description: result.message,
        })
        refetchList()
        onClose()
      } else {
        showDialog({
          title: 'Erro',
          description: result.message,
        })
      }
    }
  }

  const { data: promotersCompanySelect } = useQuery({
    queryKey: ['users_promoters_company_select'],
    queryFn: async () => {
      const { promotersCompanies } = await getAllPromotersCompanies()
      return promotersCompanies.map(item => ({ value: item.id, label: item.name }))
    },
  })

  const defineRolesSelect = useCallback(() => {
    setRolesSelect(getRolesSelectOptions(profile?.role || 'PROMOTER'))
  }, [profile])

  useEffect(() => {
    defineRolesSelect()
  }, [defineRolesSelect])

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold text-2xl">
            {user
              ? 'Editar'
              : 'Criar'} usuário
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full max-h-[56dvh] overflow-y-auto"
          onSubmit={handleSubmit(handleSubmitUpsertUserForm)}
        >
          <div>
            <Input
              placeholder="Nome"
              label="Nome"
              value={user?.name}
              readOnly
              disabled
            />
          </div>
          <div>
            <Input
              placeholder="Identificador"
              label="ID"
              value={user?.id}
              readOnly
              disabled
            />
          </div>
          <div>
            <Input
              placeholder="email@exemplo.com"
              id="email"
              label="Email"
              disabled={user?.is_active}
              required={!user?.is_active}
              {...register('email')}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="role"
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900 mb-1"
                    htmlFor="state"
                  >
                    Cargo
                    <span className="text-red-500 ml-2">
                      *
                    </span>
                  </label>
                  <Select
                    id="role"
                    options={rolesSelect}
                    onChange={state => onChange(state?.value)}
                    value={rolesSelect.filter(role => role.value === value)}
                    placeholder="Selecione..."
                  />
                </>
              )}
            />
          </div>
          {profile?.role !== 'PROMOTER_MANAGER' && (
            <div>
              <Controller
                control={control}
                name="subrole"
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900 mb-1"
                      htmlFor="state"
                    >
                      Cargo secundário
                    </label>
                    <Select
                      id="role"
                      options={[{ value: 'NONE', label: 'Nenhum' }, ...rolesSelect.filter(item => selectedRole !== item.value)]}
                      onChange={state => onChange(state?.value)}
                      value={[{ value: 'NONE', label: 'Nenhum' }, ...rolesSelect].filter(role => role.value === value)}
                      placeholder="Selecione..."
                    />
                  </>
                )}
              />
            </div>
          )}
          {(selectedRole === 'PROMOTER') && (
            <div>
              <Controller
                control={control}
                name="promoter_of_company_id"
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900 mb-1"
                      htmlFor="promoter_of_company_id"
                    >
                      Empresa de promotores
                      <span className="text-red-500 ml-2">
                        *
                      </span>
                    </label>
                    <Select
                      id="promoter_of_company_id"
                      options={promotersCompanySelect}
                      onChange={state => onChange(state?.value)}
                      value={promotersCompanySelect && promotersCompanySelect.filter(role => role.value === value)}
                      placeholder="Selecione..."
                      isDisabled={profile?.role === 'PROMOTER_MANAGER'}
                    />
                  </>
                )}
              />
            </div>
          )}
          {(selectedRole === 'PROMOTER_MANAGER') && (
            <div>
              <Controller
                control={control}
                name="manager_of_promoters_company_id"
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900 mb-1"
                      htmlFor="manager_of_promoters_company_id"
                    >
                      Empresa de promotores
                      <span className="text-red-500 ml-2">
                        *
                      </span>
                    </label>
                    <Select
                      id="manager_of_promoters_company_id"
                      options={promotersCompanySelect}
                      onChange={state => onChange(state?.value)}
                      value={promotersCompanySelect && promotersCompanySelect.filter(role => role.value === value)}
                      placeholder="Selecione..."
                      isDisabled={profile?.role === 'PROMOTER_MANAGER'}
                    />
                  </>
                )}
              />
            </div>
          )}
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
            >
              Salvar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
